import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as log from 'loglevel';

import { resolveByCountry } from '@codex/helpers';

import { getActiveListingPage, getActiveListingPageProductIds } from '../../reduck/selectors';
import RichRelevanceProductsMainContainer from '../RichRelevance/RichRelevanceProductsMainContainer';


const isRichRelevanceLoadedCorrectly = () => (
  typeof window.r3_category === 'function' &&
  typeof window.r3_search === 'function' &&
  typeof window.R3_COMMON === 'object' &&
  typeof window.RR === 'object' &&
  typeof window.rr_flush_onload === 'function' &&
  typeof window.r3 === 'function'
);

// add currency sign to price
// replace large image URL from Brain with smaller one
// convert numberOfReviews property from e. g. '(241)' to 241
// convert availableInStore and homeDelivery to object with boolean 'available' and string 'label' properties
const normalizeRichRelevanceProductsDataForUI = (richRelevanceProductsData) => (
  richRelevanceProductsData.map((item) => (
    {
      ...item,
      price: resolveByCountry('£', '€') + item.price,
      imageURL: item.imageURL.replace('/l_', '/s_'),
      numberOfReviews:
        !Number.isNaN(Number(item.numberOfReviews.replace('(', '')
          .replace(')', '')))
          ? Number(item.numberOfReviews.replace('(', '')
            .replace(')', ''))
          : 0,
      homeDelivery: {
        available: item.homeDelivery === 'true',
        label: 'Home delivery'
      },
      availableInStore: {
        available: item.availableInStore === 'true',
        label: 'Order & Collect'
      }
    }
  ))
);

const RichRelevanceBlock = (
  { pageDataSuccessfullyLoaded, groupcallLoaded, categoryId, categoryName,
    currentProductIds, session, hashedEmail, deviceType, searchListingPage,
    searchQuery, tagSearchQuery, fupidsSearchQuery, skusSearchQuery }) => {

  const [productsFromRichRelevance, setProductsFromRichRelevance] = useState([]);
  const [richRelevanceHeaderLabel, setRichRelevanceHeaderLabel] = useState('');

  useEffect(() => {
    // update RichRelevance products only if groupcall finished succesfully and there are new products on page
    if (pageDataSuccessfullyLoaded && groupcallLoaded) {
      if (!isRichRelevanceLoadedCorrectly()) {
        log.warn("RichRelevance wasn't loaded correctly and it's not available.");
        return;
      }

      // The following code sets data for rich relevance, it's implemented according to RichRelevance documentation.
      // The implementation is slightly different for for search listing page and for segment listing page
      //
      // Documentation for search listing page:
      // https://help.richrelevance.com/Integration_Guide/0010_JSON_Integration%3A_Core_Integration/0021_JSON_Integration%3A_Search_Page
      //
      // Documentation for segment listing page:
      // https://help.richrelevance.com/Integration_Guide/0010_JSON_Integration%3A_Core_Integration/0012_JSON_Integration%3A_Category_Page
      window.R3_SEARCH = undefined;
      window.R3_CATEGORY = undefined;

      if (searchListingPage) {
        // search listing page
        window.R3_SEARCH = new window.r3_search(); // eslint-disable-line new-cap
        window.R3_SEARCH.setTerms(searchQuery || tagSearchQuery || fupidsSearchQuery || skusSearchQuery || '');
        window.R3_SEARCH.itemIds = undefined;
        currentProductIds.forEach(window.R3_SEARCH.addItemId);
      } else {
        // segment listing page
        window.R3_CATEGORY = new window.r3_category(); // eslint-disable-line new-cap
        window.R3_CATEGORY.setId(categoryId);
        window.R3_CATEGORY.setName(categoryName);
        window.R3_COMMON.itemIds = undefined;
        currentProductIds.forEach(window.R3_COMMON.addItemId);
      }

      if (session) {
        window.R3_COMMON.setSessionId(session);
      }
      if (hashedEmail) {
        window.R3_COMMON.setUserId(hashedEmail);
      }

      // set RichRelevance callback
      window.RR.jsonCallback = () => {
        if (typeof window.RR.data === 'object' &&
          typeof window.RR.data.JSON === 'object' &&
          Array.isArray(window.RR.data.JSON.placements) &&
          window.RR.data.JSON.placements.length > 0) {

          if (Array.isArray(window.RR.data.JSON.placements[0].items)) {
            const convertedRRProducts = normalizeRichRelevanceProductsDataForUI(window.RR.data.JSON.placements[0].items);

            setProductsFromRichRelevance(convertedRRProducts);
          }

          if (window.RR.data.JSON.placements[0].strategy_message) {
            setRichRelevanceHeaderLabel(window.RR.data.JSON.placements[0].strategy_message);
          }
        } else {
          log.info('No products available from RichReleavnce for this page');
        }
      };

      window.rr_flush_onload();
      window.r3();

    }
  }, [pageDataSuccessfullyLoaded, groupcallLoaded]);

  return (
    productsFromRichRelevance.length > 0
      ? (
        <RichRelevanceProductsMainContainer
          title={richRelevanceHeaderLabel}
          productList={productsFromRichRelevance}
          deviceType={deviceType}
        />
      )
      : null
  );
};

RichRelevanceBlock.propTypes = {
  deviceType: PropTypes.oneOf(['wide', 'desktop', 'tablet', 'mobile']).isRequired,
  searchListingPage: PropTypes.bool.isRequired,
  searchQuery: PropTypes.string,
  tagSearchQuery: PropTypes.string,
  fupidsSearchQuery: PropTypes.string,
  skusSearchQuery: PropTypes.string,
  pageDataSuccessfullyLoaded: PropTypes.bool.isRequired,
  groupcallLoaded: PropTypes.bool.isRequired,
  currentProductIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  categoryId: PropTypes.string,
  categoryName: PropTypes.string,
  session: PropTypes.string,
  hashedEmail: PropTypes.string
};

RichRelevanceBlock.defaultProps = {
  searchQuery: '',
  tagSearchQuery: '',
  fupidsSearchQuery: '',
  skusSearchQuery: '',
  categoryId: '',
  categoryName: '',
  session: undefined,
  hashedEmail: undefined
};


const mapStateToProps = (state) => ({
  deviceType: state.listingPage.browser.device.deviceType,
  searchListingPage: getActiveListingPage(state).searchListingPage,
  searchQuery: getActiveListingPage(state)?.pageAttributes.query
    ? getActiveListingPage(state)?.pageAttributes.query
    : '',
  tagSearchQuery: getActiveListingPage(state)?.pageAttributes?.tag
    ? getActiveListingPage(state)?.pageAttributes?.tag
    : '',
  fupidsSearchQuery: getActiveListingPage(state)?.pageAttributes?.id
    ? getActiveListingPage(state)?.pageAttributes?.id
    : '',
  skusSearchQuery: getActiveListingPage(state)?.pageAttributes?.externalref
    ? getActiveListingPage(state)?.pageAttributes?.externalref
    : '',
  pageDataSuccessfullyLoaded: !state.listingPage.commonData.xhrStatus.pendingProductsRequest &&
    !state.listingPage.commonData.xhrStatus.productsRequestErrorMessage,
  groupcallLoaded: !!state.configuration.groupcallLoaded,
  currentProductIds: getActiveListingPageProductIds(state).slice(0, 15),
  categoryId: (getActiveListingPage(state).pageAttributes.filterAttributes &&
    getActiveListingPage(state).pageAttributes.filterAttributes.segment_id)
    ? getActiveListingPage(state).pageAttributes.filterAttributes.segment_id[0]
    : '',
  categoryName: getActiveListingPage(state).ucmsCategorization
    ? getActiveListingPage(state).ucmsCategorization.categoryName
    : '',
  session: state.configuration.session ? state.configuration.session : undefined,
  hashedEmail: (state.configuration.user && state.configuration.user.hashed_email)
    ? state.configuration.user.hashed_email : undefined
});


export default connect(mapStateToProps)(RichRelevanceBlock);
